import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { message } from 'antd'
import { AuthProvider } from './contexts/AuthContext'
import { SimulazioneProvider } from './contexts/SimulazioneContext'
import { SimulazioneRuolo } from './components/SimulazioneRuolo'
import { PageTitleProvider } from './contexts/PageTitleContext'
import PrivateRoute from './components/PrivateRoute'
import ProtectedRoute from './components/auth/ProtectedRoute'
import PermissionRoute from './components/PermissionRoute'
import LandingRedirect from './components/LandingRedirect'
import { LayoutSelector } from './components/layouts/LayoutSelector'
import ForbiddenPage from './pages/ForbiddenPage'
import RegisterRequestPage from './pages/auth/RegisterRequestPage'
import SetPasswordPage from './pages/auth/SetPasswordPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import { OAuthCallback } from './pages/OAuthCallback'
import DashboardPage from './pages/dashboard/DashboardPage'
import CustomersPage from './pages/customers/CustomersPage'
import VehiclesPage from './pages/vehicles/VehiclesPage'
import PartsPage from './pages/parts/PartsPage'
import TiresPage from './pages/tires/TiresPage'
import CourtesyCarsPage from './pages/courtesy-cars/CourtesyCarsPage'
import CourtesyCarPlanningPage from './pages/courtesy-cars/CourtesyCarPlanningPage'
import ConsegnaAdcPage from './pages/courtesy-cars/ConsegnaAdcPage'
import SuppliersPage from './pages/suppliers/SuppliersPage'
import PurchaseRequestsPage from './pages/purchase-requests/PurchaseRequestsPage'
import OrdiniPage from './pages/ordini/OrdiniPage'
import MaintenanceSchedulesPage from './pages/maintenance/MaintenanceSchedulesPage'
import CalendarPage from './pages/calendar/CalendarPage'
import NotificationsPage from './pages/notifications/NotificationsPage'
import BIPage from './pages/bi/BIPage'
import ConsumiAiPage from './pages/ai-usage/ConsumiAiPage'
import UsersPage from './pages/users/UsersPage'
import ProfilePage from './pages/profile/ProfilePage'
import SettingsPage from './pages/settings/SettingsPage'
import NotFoundPage from './pages/NotFoundPage'
import SchedeLavoroBeta from './pages/schede-lavoro/SchedeLavoroBeta'
import SchedaLavoroV2 from './pages/schede-lavoro/SchedaLavoroV2'
import DettaglioScheda from './pages/schede-lavoro/DettaglioScheda'
import RedirectDettaglioNuova from './pages/schede-lavoro/RedirectDettaglioNuova'
import OfficinePage from './pages/officine/OfficinePage'
import CostiTariffePage from './pages/costi-tariffe/CostiTariffePage'
import AcquistiPage from './pages/acquisti/AcquistiPage'
import DettaglioAcquistoPage from './pages/acquisti/DettaglioAcquistoPage'
import RdoTemplatePage from './pages/rdo-templates/RdoTemplatePage'
import TireServicePage from './pages/tire-service/TireServicePage'
import TireCheckinPage from './pages/tire-service/TireCheckinPage'
import TabletTireCheckin from './pages/tire-service/tablet/TabletTireCheckin'
import TabletTireWorkOrder from './pages/tire-service/tablet/TabletTireWorkOrder'
import TabletTireMagazzino from './pages/tire-service/tablet/TabletTireMagazzino'
import TireCheckinPublicPage from './pages/tire-service/TireCheckinPublicPage'
import ComunicazioniPage from './pages/comunicazioni/ComunicazioniPage'
import ImpostazioniIntegrazioni from './pages/integrazioni/ImpostazioniIntegrazioni'
import BoardGiornalieroPage from './pages/board-giornaliero/BoardGiornalieroPage'
import PreAccettazionePage from './pages/pre-accettazione/PreAccettazionePage'
import RichiestePreventivoPage from './pages/officina/RichiestePreventivoPage'
import PreventiviOfficinePage from './pages/preventivi-officine/PreventiviOfficinePage'
import DiagnosticaGatePage from './pages/diagnostica-gate/DiagnosticaGatePage'

function App() {
  // Configura message globale
  useEffect(() => {
    message.config({
      top: 100,
      duration: 3,
      maxCount: 3,
    })
  }, [])

  // Rende TUTTE le modali Ant Design trascinabili.
  // MutationObserver: attacca drag direttamente su ogni .ant-modal-header
  // quando appare nel DOM — bypassa qualsiasi stopPropagation interno di Ant Design.
  // Trasla .ant-modal-content (evita conflitti con animazioni rc-motion su .ant-modal).
  useEffect(() => {
    const attachDrag = (header: HTMLElement) => {
      if ((header as any).__dragAttached) return
      ;(header as any).__dragAttached = true

      const content = header.closest<HTMLElement>('.ant-modal-content')
      if (!content) return

      let drag: { startX: number; startY: number; tx: number; ty: number } | null = null

      const onDown = (e: MouseEvent) => {
        const t = e.target as HTMLElement
        if (t.closest('button') || t.closest('a') || t.closest('input') ||
            t.closest('textarea') || t.closest('select') || t.closest('.ant-modal-close')) return
        const ts = content.style.transform
        const m = (ts && ts !== 'none') ? new DOMMatrix(ts) : new DOMMatrix()
        drag = { startX: e.clientX, startY: e.clientY, tx: m.m41, ty: m.m42 }
        header.style.cursor = 'grabbing'
        e.preventDefault()
      }

      const onMove = (e: MouseEvent) => {
        if (!drag) return
        content.style.transform = `translate(${drag.tx + e.clientX - drag.startX}px, ${drag.ty + e.clientY - drag.startY}px)`
      }

      const onUp = () => {
        if (!drag) return
        header.style.cursor = 'grab'
        drag = null
      }

      header.addEventListener('mousedown', onDown)
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)

      ;(header as any).__dragCleanup = () => {
        header.removeEventListener('mousedown', onDown)
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
    }

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach(node => {
          if (!(node instanceof HTMLElement)) return
          const headers = node.matches('.ant-modal-header')
            ? [node]
            : Array.from(node.querySelectorAll<HTMLElement>('.ant-modal-header'))
          headers.forEach(attachDrag)
        })
        m.removedNodes.forEach(node => {
          if (!(node instanceof HTMLElement)) return
          const headers = node.matches('.ant-modal-header')
            ? [node]
            : Array.from(node.querySelectorAll<HTMLElement>('.ant-modal-header'))
          headers.forEach(h => (h as any).__dragCleanup?.())
        })
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  return (
    <SimulazioneProvider>
    <AuthProvider>
      <SimulazioneRuolo />
      <PageTitleProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/register" element={<RegisterRequestPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/tire-service/checkin/:codice" element={<TireCheckinPublicPage />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute><PrivateRoute /></ProtectedRoute>}>
            <Route element={<LayoutSelector />}>
              <Route path="/" element={<LandingRedirect />} />
              <Route path="/dashboard" element={<PermissionRoute permission="dashboard.view"><DashboardPage /></PermissionRoute>} />
              <Route path="/customers" element={<PermissionRoute permission="customers.view"><CustomersPage /></PermissionRoute>} />
              <Route path="/vehicles" element={<PermissionRoute permission="vehicles.view"><VehiclesPage /></PermissionRoute>} />
              <Route path="/parts" element={<PermissionRoute permission="parts.view"><PartsPage /></PermissionRoute>} />
              <Route path="/tires" element={<PermissionRoute permission="tires.view"><TiresPage /></PermissionRoute>} />
              <Route path="/suppliers" element={<PermissionRoute permission="suppliers.view"><SuppliersPage /></PermissionRoute>} />
              <Route path="/rda" element={<PermissionRoute permission="purchase_requests.view"><PurchaseRequestsPage /></PermissionRoute>} />
              <Route path="/ordini" element={<PermissionRoute permission="purchase_requests.receive"><OrdiniPage /></PermissionRoute>} />
              <Route path="/courtesy-cars" element={<PermissionRoute permission="courtesy_cars.view"><CourtesyCarsPage /></PermissionRoute>} />
              <Route path="/courtesy-cars/planning" element={<PermissionRoute permission="courtesy_cars.view"><CourtesyCarPlanningPage /></PermissionRoute>} />
              <Route path="/courtesy-cars/consegna" element={<PermissionRoute permission="courtesy_cars.loan"><ConsegnaAdcPage /></PermissionRoute>} />
              <Route path="/maintenance" element={<PermissionRoute permission="maintenance.view"><MaintenanceSchedulesPage /></PermissionRoute>} />
              <Route path="/calendar" element={<PermissionRoute permission="calendar.view"><CalendarPage /></PermissionRoute>} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/bi" element={<PermissionRoute permission="analisi.view"><BIPage /></PermissionRoute>} />
              <Route path="/consumi-ai" element={<PermissionRoute permission="ai_usage.view"><ConsumiAiPage /></PermissionRoute>} />
              <Route path="/users" element={<PermissionRoute permission="system.manage_users"><UsersPage /></PermissionRoute>} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/settings" element={<PermissionRoute permission="system.settings"><SettingsPage /></PermissionRoute>} />
              <Route path="/schede-lavoro" element={<PermissionRoute permission={['work_orders.view_own', 'work_orders.view']}><SchedeLavoroBeta /></PermissionRoute>} />
              {/* Dal 04/09/2026 il dettaglio predefinito e' la V3. Lo stesso
                  percorso serve anche la creazione (/schede-lavoro/new), che
                  resta sulla V2: sceglie DettaglioScheda, che ha bisogno del
                  parametro :id per distinguere i due casi. */}
              <Route path="/schede-lavoro/:id" element={<PermissionRoute permission={['work_orders.view_own', 'work_orders.view']}><DettaglioScheda /></PermissionRoute>} />
              {/* Rete di sicurezza: la pagina classica resta raggiungibile. */}
              <Route path="/schede-lavoro/:id/classica" element={<PermissionRoute permission={['work_orders.view_own', 'work_orders.view']}><SchedaLavoroV2 /></PermissionRoute>} />
              {/* Compatibilita': la vecchia rotta di collaudo rimanda al dettaglio. */}
              <Route path="/schede-lavoro/:id/nuova" element={<PermissionRoute permission={['work_orders.view_own', 'work_orders.view']}><RedirectDettaglioNuova /></PermissionRoute>} />
              <Route path="/officine" element={<PermissionRoute permission="system.manage_workshops"><OfficinePage /></PermissionRoute>} />
              <Route path="/costi-tariffe" element={<PermissionRoute permission="costs.manage_rates"><CostiTariffePage /></PermissionRoute>} />
              <Route path="/acquisti" element={<PermissionRoute permission="purchase_requests.view"><AcquistiPage /></PermissionRoute>} />
              <Route path="/acquisti/:interventionId" element={<PermissionRoute permission="purchase_requests.view"><DettaglioAcquistoPage /></PermissionRoute>} />
              <Route path="/richieste-preventivo" element={<PermissionRoute permission="workshop_quotes.respond"><RichiestePreventivoPage /></PermissionRoute>} />
              <Route path="/preventivi-officine" element={<PermissionRoute permission="workshop_quotes.view"><PreventiviOfficinePage /></PermissionRoute>} />
              <Route path="/rdo-templates" element={<PermissionRoute permission="rdo_templates.view"><RdoTemplatePage /></PermissionRoute>} />
              <Route path="/integrazioni" element={<PermissionRoute permission="system.settings"><ImpostazioniIntegrazioni /></PermissionRoute>} />
              {/* Tire Service — desktop */}
              <Route path="/tire-service" element={<PermissionRoute permission="tire_service.view"><TireServicePage /></PermissionRoute>} />
              <Route path="/tire-checkin" element={<PermissionRoute permission="tire_service.checkin"><TireCheckinPage /></PermissionRoute>} />
              <Route path="/comunicazioni" element={<ComunicazioniPage />} />
              <Route path="/board-giornaliero" element={<PermissionRoute permission="board_giornaliero.view"><BoardGiornalieroPage /></PermissionRoute>} />
              <Route path="/pre-accettazione" element={<PermissionRoute permission="pre_accettazione.create"><PreAccettazionePage /></PermissionRoute>} />
              <Route path="/forbidden" element={<ForbiddenPage />} />
              <Route path="/diagnostica-gate" element={<PermissionRoute permission="system.manage_roles"><DiagnosticaGatePage /></PermissionRoute>} />
            </Route>
          </Route>

          {/* Tire Service — tablet (layout proprio, no LayoutSelector) */}
          <Route element={<ProtectedRoute><PrivateRoute /></ProtectedRoute>}>
            <Route path="/tablet/tire-checkin" element={<PermissionRoute permission="tire_service.checkin"><TabletTireCheckin /></PermissionRoute>} />
            <Route path="/tablet/tire-workorder/:id" element={<PermissionRoute permission="tire_service.checkin"><TabletTireWorkOrder /></PermissionRoute>} />
            <Route path="/tablet/tire-magazzino" element={<PermissionRoute permission="tire_service.view"><TabletTireMagazzino /></PermissionRoute>} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </PageTitleProvider>
    </AuthProvider>
    </SimulazioneProvider>
  )
}

export default App
